import { FC, ReactElement, useState } from 'react';

import { useDebounce } from 'react-use';

import { NewsletterCallToAction } from '../../../containers';
import { NewsletterCallToActionInterface } from '../../../entities/@blocks/NewsletterCallToAction/NewsletterCallToAction';
import { NewsletterFormData } from '../../../entities/@forms/NewsletterForm/NewsletterForm';
import { GTMEvent } from '../../../entities/GTM/GTM';
import { sendGTMEvent } from '../../../entities/GTM/GTMService';
import { signUpForNewsletter } from '../../../redux/@forms/newsletterForm/newsletterFormActions';
import { fetchStoreLocationSearchResults } from '../../../redux/@stores/stores/storesActions';
import { useTypedDispatch, useTypedSelector } from '../../../redux/store';

export interface ConnectedNewsletterCallToActionProps extends NewsletterCallToActionInterface {
    className?: string;
}

export const ConnectedNewsletterCallToAction: FC<ConnectedNewsletterCallToActionProps> = ({
    className = '',
    ...newsletterCallToActionProps
}): ReactElement => {
    const dispatch = useTypedDispatch();

    const storeLocations = useTypedSelector(state => state.storesSlice.storeLocations);
    const searchResults = useTypedSelector(state => state.storesSlice.searchResults);

    const isLoadingForm = useTypedSelector(state => state.newsletterFormSlice.isLoading);
    const formError = useTypedSelector(state => state.newsletterFormSlice.error);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [hasSelectedResult, setHasSelectedResult] = useState<boolean>(false);

    useDebounce((): void => {
        if (searchQuery && !hasSelectedResult) {
            dispatch(fetchStoreLocationSearchResults(searchQuery));
        }

        setHasSelectedResult(false);
    }, 250, [searchQuery]);

    const handleResultClick = (): void => {
        setHasSelectedResult(true);
    };

    const handleFormSubmit = async (formData: NewsletterFormData): Promise<void> => {
        await dispatch(signUpForNewsletter(formData));

        sendGTMEvent(GTMEvent.signUp, {
            method: 'newsletter',
        });
    };

    return (
        <NewsletterCallToAction
            {...newsletterCallToActionProps}
            isLoadingForm={isLoadingForm}
            searchQuery={searchQuery}
            storeLocations={storeLocations}
            searchResults={searchResults}
            formError={formError}
            onSearchChange={setSearchQuery}
            onResultClick={handleResultClick}
            onFormSubmit={handleFormSubmit}
            className={className}
        />
    );
};
